// src/pages/UserManual.js

import React, { useState } from 'react';
import { Button, Divider, List, Typography, Card, Image } from 'antd';
// eslint-disable-next-line
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

export default function UserManual() {
  const [selectedSection, setSelectedSection] = useState('general');

  // --- General Coverage Platform Instructions ---
  const generalInstructions = (
    <div>
      <Title level={3}>Before You Start</Title>
      <List
        split={false}                // remove dividers
        dataSource={[
          <span>
            To successfully check medication coverage, you’ll need your patient’s Medicare card.&nbsp;
            Locate the Medicare Plan ID—it typically begins with an{' '}
            <Text strong style={{ fontSize: 16 }}>H</Text>,{' '}
            <Text strong style={{ fontSize: 16 }}>R</Text>, or{' '}
            <Text strong style={{ fontSize: 16 }}>S</Text>, followed by 7 or 10 digits
            (e.g. <Text code>H6158‑001</Text> or <Text code>H6158‑001‑002</Text>).
          </span>,
          <span>
            It usually appears on the right‑bottom of the front side of the card.
          </span>,
          <span>
            <Text strong style={{ fontSize: 16 }}>Humana</Text> plans show it after “CMS”; <Text strong style={{ fontSize: 16 }}>Cigna</Text> plans display it at the top‑left.
          </span>,
          <span>
            Here are some card examples to help you find the Plan ID:
          </span>,
        ]}
        renderItem={item => (
          <List.Item
            style={{
              padding: 0,
              marginBottom: 16,      // add space between lines
              fontSize: 16,
              lineHeight: 1.6
            }}
          >
            {item}
          </List.Item>
        )}
        style={{ marginBottom: 32 }}  // extra space after the whole block
      />


      {/* Inline clickable previews */}
      <Image.PreviewGroup>
        <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
          <Image
            width={400}
            src="/AARP.png"
            alt="Medicare Plan ID example"
            style={{ cursor: 'pointer' }}
          />
          <Image
            width={400}
            src="/Humana.png"
            alt="Humana card example"
            style={{ cursor: 'pointer' }}
          />
          <Image
            width={400}
            src="/Cigna.png"
            alt="Cigna card example"
            style={{ cursor: 'pointer' }}
          />
          
        </div>
      </Image.PreviewGroup>
  
      <Divider />
  
      <Title level={3}>Step‑by‑Step Guide to Using the Platform</Title>

      <Title level={4}>Step 1: Choose Search Type</Title>
      <List
        dataSource={[
          <span>
              Select from the left-side tabs to search medication coverage by <Text strong style={{ fontSize: 16 }}>Drug Class</Text> or by <Text strong style={{ fontSize: 16 }}>Individual Drug Name</Text>.
          </span>,
        ]}
        renderItem={item => (
          <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
            {item}
          </List.Item>
        )}
        style={{ marginBottom: 24 }}
      />

      <Title level={4}>Step 2: Select Insurance Identification Method</Title>
        <List
          dataSource={[
            <span>
                Choose your preferred method: search by <Text strong style={{ fontSize: 16 }}>Plan ID</Text> or  <Text strong style={{ fontSize: 16 }}>Insurance Name</Text>.
            </span>,
                  <Text italic >
                  Note: When Search by Insurance Name: There will be a formulary number with each plan name; although plans have different names, they sometimes share the same formulary, so it will help you to get the right coverage information as well.
                </Text>
          ]}
          renderItem={item => (
            <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
              {item}
            </List.Item>
          )}
          style={{ marginBottom: 24 }}
      />
      <Title level={4}>Step 3: Enter Search Details</Title>
        <List
        dataSource={[
          <span>
              1️⃣ Input the patient's Zip Code.
          </span>,
          <span>
              2️⃣ Provide the&nbsp;
              <Text strong style={{ fontSize: 16 }}>Medicare Plan ID</Text> or select the <Text strong style={{ fontSize: 16 }}>Insurance Name</Text>.
          </span>,
          <span>
            3️⃣ Enter the specific <Text strong style={{ fontSize: 16 }}>Drug Class</Text> or <Text strong style={{ fontSize: 16 }}>Drug Name</Text>, then click <Text strong style={{ fontSize: 16 }}>"Search"</Text>.
          </span>,
        ]}
        renderItem={item => (
          <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
            {item}
          </List.Item>
        )}
        style={{ marginBottom: 24 }}
      />

      <Title level={4}>Step 4: Review Coverage Information </Title>
        <List
        dataSource={[
          <span>
            You'll see detailed coverage results including:
          </span>,
              <span>
                  1️⃣ Medication name and Coverage tier level.
              </span>,
              <span>
                  2️⃣ Patient’s <Text strong style={{ fontSize: 16 }}> out-of-pocket costs at each coverage stage.</Text> 
              </span>,
              <span>
                3️⃣ Coverage requirements (such as <Text strong style={{ fontSize: 16 }}>Prior Authorization (PA) or Step Therapy (ST)</Text>)before medication coverage.
              </span>,
        ]}
        renderItem={item => (
          <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
            {item}
          </List.Item>
        )}
        style={{ marginBottom: 24 }}
      />

      <Title level={4}>Step 5: Click 'View Monthly Breakdown' </Title>
        <List
        dataSource={[
          <span>
            To examine detailed costs across time, click <Text strong style={{ fontSize: 16 }}>"View Monthly Breakdown"</Text> located under the drug details. This helps you quickly:
          </span>,
        <span>
            1️⃣ Check medication coverage and forecast costs across multiple refills.
        </span>,
        <span>
            2️⃣ Estimate future expenses <Text strong style={{ fontSize: 16 }}>with and without Medicare Prescription Payment Plan (M3P) costs.</Text>
        </span>,
        <span>
          3️⃣ Clearly see when patients <Text strong style={{ fontSize: 16 }}>reach $2,000 cap.</Text>
        </span>,
            ]}
        renderItem={item => (
          <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
            {item}
          </List.Item>
        )}
        style={{ marginBottom: 24 }}
      />
      <img
      src="/monthly-breakdown.gif"
      alt="monthly-breakdown"
      style={{
        display: 'block',
        width: '100%',
        maxWidth: 1000,
        margin: 'auto 0 24px',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
      }}
    />
      <Divider />
        <Title level={4}>Important Notes on Drug Costs: </Title>
        <List
        dataSource={[
          <span>
            ❕Estimates Only: Displayed prices are approximate and can vary at pharmacies.
          </span>,
        <span>
            ❕Accuracy by County: If no cost appears initially, try using the patient's home zip code.
        </span>,
        <span>
           ❕Data Availability: We cover <Text strong style={{ fontSize: 16 }}>commonly prescribed brand and generic medications</Text>. If a cost isn't immediately available, it will typically be updated within 24 hours.
        </span>,
            ]}
        renderItem={item => (
          <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
            {item}
          </List.Item>
        )}
        style={{ marginBottom: 24 }}
      />

      <Title level={5}>
      We hope this guide helps you efficiently use MedHug platform to provide accurate medication coverage and cost estimates for your patients!
      </Title>
      <Divider />
  
      {/* Tutorial video placeholder */}
      <Card bordered={false}>
        <Title level={3}>Coverage Platform Tutorial Video</Title>
        <Paragraph>
          Watch this video for a full walkthrough of Coverage Platform:
        </Paragraph>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <iframe
            src="https://www.youtube.com/embed/RkjVbO_zibQ?si=_eiz0Uf15qXR37Wn" 
            title="Platform Tutorial"           
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '100%', height: '100%'
            }}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Card>
    </div>
  );
  // --- M3P Tool Instructions ---
  const m3pInstructions = (
  <div>
    <Title level={3}>Using the M3P Tool</Title>

    <Paragraph>
      The <Text strong>Medicare Prescription Payment Plan (M3P) Tool</Text> lets you:
    </Paragraph>
    <List
      size="small"
      dataSource={[
        '✅ Check medication coverage under the new M3P program',
        '✅ Estimate patient costs with or without M3P',
        '✅ See if participating in M3P will benefit your patients',
      ]}
      renderItem={item => <List.Item>{item}</List.Item>}
      style={{ marginBottom: 24 }}
    />

    <Paragraph>
      <Text italic >
        Note: To get the most accurate estimate, include all prescriptions (especially high‑cost brands) from January 1, 2025 onward. The tool will then compute patients' monthly bill, accumulated out‑of‑pocket spending, and time to hit the $2,000 cap.
      </Text>
    </Paragraph>

    {/* Step 1 */}
    <Title level={4}>Step 1: Enter Plan & Medication Information</Title>
    <List
      dataSource={[
        <span>
            1️⃣ For each drug, enter the&nbsp;
          <Text strong>Drug Name, refill Frequency, and prescribed start date</Text>.
        </span>,
        <span>
            2️⃣ Include all medications taken since the start of&nbsp;
          <Text strong>2025</Text> for best results.
        </span>,
        <span>
           3️⃣ Choose the &nbsp;
          <Text strong>Payment Plan Start Month</Text>, then click “Search.”
        </span>,
      ]}
      renderItem={item => (
        <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
          {item}
        </List.Item>
      )}
      style={{ marginBottom: 24 }}
    />
    <img
      src="/m3p-input.gif"
      alt="M3P Tool Input Demo"
      style={{
        display: 'block',
        width: '100%',
        maxWidth: 1000,
        margin: 'auto 0 24px',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
      }}
    />

    {/* Step 2 */}
    <Title level={4}>Step 2: Instantly Review Cost Estimates</Title>
    <List
      dataSource={[
        <span>
            1️⃣ Drug Cost Breakdown: Detailed per‑fill costs and coverage phases.
        </span>,
        <span>
            2️⃣ Monthly Breakdown Table: Compare what patients pay
          <Text strong>with and without M3P each month.</Text>
        </span>,
        <span>
           3️⃣ Monthly Payment Trend Graph: 
          <Text strong>Visualize your out‑of‑pocket vs. M3P bills</Text>, over time.
        </span>,
      ]}
      renderItem={item => (
        <List.Item style={{ fontSize: '16px', lineHeight: 1.5 }}>
          {item}
        </List.Item>
      )}
      style={{ marginBottom: 24 }}
      />
    <img
      src="/m3p-results.gif"
      alt="M3P Tool Results Demo"
      style={{
        display: 'block',
        width: '100%',
        maxWidth: 1000,
        margin: 'auto 0 24px',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
      }}
    />

    <Title level={5}>
      You can freely tweak your drug list, refill frequency, or start date to compare scenarios and find the optimal solution for your patients.
    </Title>
              {/* New link for basic info and FAQs */}
          <div style={{ marginBottom: 16 }}>
            <Link to="/m3pinfo" style={{ textDecoration: 'underline', color: '#007bff', fontSize: '16px' }}>
              Click to learn more M3P basics and FAQs 
            </Link>
          </div>

    <Divider />

      <Card bordered={false}>
        <Title level={3}>Tutorial Video</Title>
        <Paragraph>
          Watch this video for a full walkthrough of M3P Tool:
        </Paragraph>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <iframe
            src="https://www.youtube.com/embed/pNWgstOe7XQ"
            title="M3P Overview Video"
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '100%', height: '100%'
            }}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Card>
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: 'auto', lineHeight: '1.6', fontSize: '16px'  }}>
      <Title>User Manual</Title>
      <Paragraph>
        Select an option below to view instructions on how to use the platform.
      </Paragraph>
      
      {/* Section selector */}
      <div style={{ marginBottom: '24px' }}>
        <Button
          type={selectedSection === 'general' ? 'primary' : 'default'}
          onClick={() => setSelectedSection('general')}
          style={{ marginRight: '8px' }}
        >
          General Coverage Platform
        </Button>
        <Button
          type={selectedSection === 'm3p' ? 'primary' : 'default'}
          onClick={() => setSelectedSection('m3p')}
        >
          M3P Tool
        </Button>
      </div>

      <Divider />

      {/* Show section based on selection */}
      {selectedSection === 'general' ? generalInstructions : m3pInstructions}

    </div>
  );
}
